(function () {
  'use strict';

  class ProductCarousel {
    constructor(container) {
      this.container = container;
      this.carousel  = container.querySelector('[data-product-carousel]');
      this.slides    = Array.from(container.querySelectorAll('.carousel-slide'));
      this.dots      = container.querySelectorAll('.carousel-dot');
      this.prevBtn   = container.querySelector('[data-carousel-prev]');
      this.nextBtn   = container.querySelector('[data-carousel-next]');
      this.currentIndex = 0;

      this.modal              = null;
      this.modalCloseBtn      = null;
      this.modalPrevBtn       = null;
      this.modalNextBtn       = null;
      this.modalSlideContainer = null;
      this.modalBuilt         = false;

      this.fullscreenImagesData  = null;
      this.hasFullscreenOverride = false;
      this.loadFullscreenImagesData();

      this.modalIndex     = 0;
      this.modalSlides    = [];
      this.isModalOpen    = false;

      this.activeVideoPlayer  = null;
      this.videoPlayers       = new Map();
      this.modalVideoTimeouts = new Map();
      this.modalZoomLevels    = new Map();
      this.minZoomLevel       = 1;
      this.maxZoomLevel       = 5;
      this.zoomStep           = 0.3;
      this.modalTouchState    = new Map();
      this.modalSwipeStartX   = 0;
      this.modalSwipeStartY   = 0;
      this.touchStartX        = 0;
      this.touchStartY        = 0;
      this.touchThreshold     = 50;

      this.init();
    }

    loadFullscreenImagesData() {
      const el = document.getElementById('fullscreen-images-data');
      if (!el) return;
      try {
        this.fullscreenImagesData  = JSON.parse(el.textContent);
        this.hasFullscreenOverride = true;
      } catch (_) {}
    }

    getFullscreenImageForProductMedia(mediaIndex) {
      if (this.hasFullscreenOverride && this.fullscreenImagesData?.[mediaIndex]) {
        return this.fullscreenImagesData[mediaIndex].url;
      }
      const slide = this.slides[mediaIndex];
      return slide ? slide.getAttribute('data-fullscreen-image') : null;
    }

    init() {
      this.setupCarousel();
      this.populateThumbnails();
      this.setupTouchSwipe();
      this.setupInlineDoubleClickZoom();
      this.updateCarousel();
      this.hideSkeleton();

      requestAnimationFrame(() => requestAnimationFrame(() => this.tryPlayMetaVideo()));
    }

    setupInlineDoubleClickZoom() {
      this.slides.forEach((slide) => {
        const img = slide.querySelector('.carousel-image');
        if (!img) return;
        img.addEventListener('dblclick', (e) => {
          e.preventDefault();
          this.ensureModalReady();
          if (this.modalBuilt) {
            this.setModalZoom(this.modalIndex, 2);
          }
        });
      });
    }

    populateThumbnails() {
      if (!this.dots || this.dots.length === 0) return;

      this.dots.forEach((dot, i) => {
        if (dot.querySelector('img')) return;

        const slide = this.slides[i];
        if (!slide) return;

        const slideType = slide.getAttribute('data-slide-type');
        let thumbSrc = '';

        if (slideType === 'video') {
          thumbSrc = slide.getAttribute('data-thumbnail-video-thumb')
            || slide.getAttribute('data-fullscreen-video-thumb')
            || '';
          if (!thumbSrc) {
            const vimg = slide.querySelector('.video-thumbnail, .carousel-image');
            thumbSrc = vimg ? (vimg.currentSrc || vimg.src) : '';
          }
        } else {
          const img = slide.querySelector('.carousel-image');
          thumbSrc = img ? (img.currentSrc || img.src) : (slide.getAttribute('data-fullscreen-image') || '');
        }

        if (thumbSrc) {
          const im = document.createElement('img');
          im.src = thumbSrc;
          im.loading = 'lazy';
          im.alt = '';
          dot.appendChild(im);
        }
      });

      const populatedCount = Array.from(this.dots).filter((d) => d.querySelector('img')).length;
      if (populatedCount > 1) {
        this.container.setAttribute('data-has-thumb-rail', '');
      }
    }

    hideSkeleton() {
      const s = this.container.querySelector('.pmc-skeleton');
      if (s) s.style.display = 'none';
    }

    tryPlayMetaVideo() {
      const video = this.container.querySelector('.meta-mp4-video');
      if (!video) return;
      video.muted = true;
      video.play().catch(() => {
        document.addEventListener('click', () => video.play(), { once: true });
      });
    }

    handleMetaVideoOnSlideChange(newIndex) {
      const video = this.container.querySelector('.meta-mp4-video');
      if (!video) return;
      if (newIndex === 0) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }

    setupTouchSwipe() {
      if (!this.carousel) return;
      this.carousel.addEventListener('touchstart', (e) => {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
      }, { passive: true });
      this.carousel.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - this.touchStartX;
        const dy = e.changedTouches[0].clientY - this.touchStartY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > this.touchThreshold) {
          dx < 0 ? this.next() : this.prev();
        }
      }, { passive: true });
    }

    setupCarousel() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goToSlide(i)));

      this.slides.forEach((slide, index) => {
        const playBtn = slide.querySelector('[data-play-video]');
        if (playBtn) {
          playBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.playCarouselVideo(index, true);
          });
        }
      });

      this.slides.forEach((slide, index) => {
        const img = slide.querySelector('.carousel-image');
        if (img) {
          img.addEventListener('click', () => {
            this.currentIndex = index;
            this.updateCarousel();
            this.openModal();
          });
        }

        if (slide.classList.contains('meta-video-slide')) {
          const videoWrapper = slide.querySelector('.video-slide-wrapper');
          if (videoWrapper) {
            videoWrapper.style.cursor = 'pointer';
            videoWrapper.addEventListener('click', (e) => {
              const video = videoWrapper.querySelector('video');
              if (video) {
                const rect = video.getBoundingClientRect();
                if (e.clientY > rect.bottom - 40) return;
              }
              this.currentIndex = index;
              this.updateCarousel();
              this.openModal();
            });
          }
        }
      });

      this.slides.forEach((slide, index) => {
        if (!slide.classList.contains('video-slide')) return;
        const videoId       = slide.getAttribute('data-video-id');
        const shouldAutoPlay = slide.getAttribute('data-video-autoplay') === 'true';
        if (videoId) {
          this.videoPlayers.set(index, {
            slide, videoId, shouldAutoPlay,
            isPlaying: false, iframe: null, autoPlayTimeout: null
          });
        }
      });
    }

    ensureModalReady() {
      if (this.modal) return;

      const tpl = document.getElementById('fullscreen-modal-tpl');
      if (!tpl) return;

      // (2026-08-04) Fix: previously appended to this.container, which lives
      // inside the sticky-positioned .product-media-sticky-col. A sticky
      // ancestor creates its own CSS stacking context, so the modal's
      // z-index:9999 was only being evaluated *within* that trapped context
      // instead of against the whole page — meaning sibling content outside
      // it (variant selectors, quantity buttons, the promo card) could paint
      // over the modal, including its own close button. Appending directly
      // to document.body escapes any such ancestor entirely, same as how
      // any real modal/lightbox library "portals" its markup.
      const fragment = tpl.content.cloneNode(true);
      const modalEl = fragment.querySelector('[data-fullscreen-modal]') || fragment.firstElementChild;
      if (!modalEl) return;
      document.body.appendChild(modalEl);

      this.modal               = modalEl;
      this.modalCloseBtn       = this.modal.querySelector('[data-fullscreen-close]');
      this.modalPrevBtn        = this.modal.querySelector('[data-modal-prev]');
      this.modalNextBtn        = this.modal.querySelector('[data-modal-next]');
      this.modalSlideContainer = this.modal.querySelector('[data-modal-slide-container]');

      if (!this.modal) return;

      this.setupModal();
    }

    setupModal() {
      if (this.modalCloseBtn) this.modalCloseBtn.addEventListener('click', () => this.closeModal());
      if (this.modalPrevBtn)  this.modalPrevBtn.addEventListener('click',  () => this.modalPrev());
      if (this.modalNextBtn)  this.modalNextBtn.addEventListener('click',  () => this.modalNext());

      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal || e.target.classList.contains('fullscreen-modal-background')) {
          this.closeModal();
        }
      });

      if (this.modalSlideContainer) {
        this.modalSlideContainer.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;
          this.modalSwipeStartX = e.touches[0].clientX;
          this.modalSwipeStartY = e.touches[0].clientY;
        }, { passive: true });
        this.modalSlideContainer.addEventListener('touchend', (e) => {
          if (!this.isModalOpen || e.changedTouches.length !== 1) return;
          if (this.getModalZoom(this.modalIndex) > 1) return;
          const dx = e.changedTouches[0].clientX - this.modalSwipeStartX;
          const dy = e.changedTouches[0].clientY - this.modalSwipeStartY;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > this.touchThreshold) {
            dx < 0 ? this.modalNext() : this.modalPrev();
          }
        }, { passive: true });
      }

      document.addEventListener('keydown', (e) => {
        if (!this.isModalOpen) return;
        switch (e.key) {
          case 'Escape':    this.closeModal(); break;
          case 'ArrowLeft': this.modalPrev();  break;
          case 'ArrowRight':this.modalNext();  break;
          case '+': case '=': this.changeModalZoom(this.modalIndex,  this.zoomStep); break;
          case '-': case '_': this.changeModalZoom(this.modalIndex, -this.zoomStep); break;
          case '0':           this.resetModalZoom(this.modalIndex); break;
        }
      });
    }

    playCarouselVideo(index, immediate = false) {
      const vd = this.videoPlayers.get(index);
      if (!vd) return;
      if (vd.autoPlayTimeout) { clearTimeout(vd.autoPlayTimeout); vd.autoPlayTimeout = null; }
      this.stopAllVideos();
      if (immediate) {
        this.executeVideoPlay(index);
      } else if (vd.shouldAutoPlay) {
        vd.autoPlayTimeout = setTimeout(() => this.executeVideoPlay(index), 2000);
      }
    }

    executeVideoPlay(index) {
      const vd = this.videoPlayers.get(index);
      if (!vd) return;
      const { slide, videoId } = vd;
      const iframeContainer    = slide.querySelector('[data-video-iframe-container]');
      const thumbContainer     = slide.querySelector('.video-thumbnail-container');
      const playButton         = slide.querySelector('.video-play-button');
      if (playButton) playButton.style.display = 'none';
      iframeContainer.innerHTML = '';
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1&enablejsapi=1`;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.style.opacity = '0';
      iframeContainer.appendChild(iframe);
      iframeContainer.style.display = 'block';
      requestAnimationFrame(() => {
        iframe.style.opacity = '1';
        setTimeout(() => { if (thumbContainer) thumbContainer.style.opacity = '0'; }, 300);
      });
      vd.isPlaying = true;
      this.activeVideoPlayer = index;
    }

    stopCarouselVideo(index) {
      const vd = this.videoPlayers.get(index);
      if (!vd || !vd.isPlaying) return;
      if (vd.autoPlayTimeout) { clearTimeout(vd.autoPlayTimeout); vd.autoPlayTimeout = null; }
      const { slide } = vd;
      const iframeContainer = slide.querySelector('[data-video-iframe-container]');
      const thumbContainer  = slide.querySelector('.video-thumbnail-container');
      const playButton      = slide.querySelector('.video-play-button');
      iframeContainer.innerHTML = '';
      iframeContainer.style.display = 'none';
      if (thumbContainer) { thumbContainer.style.display = 'block'; thumbContainer.style.opacity = '1'; }
      if (playButton) playButton.style.display = 'block';
      vd.isPlaying = false;
      if (this.activeVideoPlayer === index) this.activeVideoPlayer = null;
    }

    stopAllVideos() {
      this.videoPlayers.forEach((_, index) => this.stopCarouselVideo(index));
    }

    checkAndAutoPlayVideo(index) {
      const vd = this.videoPlayers.get(index);
      if (vd && vd.shouldAutoPlay && !vd.isPlaying) this.playCarouselVideo(index, false);
    }

    prev() {
      if (this.activeVideoPlayer !== null) this.stopCarouselVideo(this.activeVideoPlayer);
      this.currentIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
      this.updateCarousel();
      this.handleMetaVideoOnSlideChange(this.currentIndex);
      this.checkAndAutoPlayVideo(this.currentIndex);
    }

    next() {
      if (this.activeVideoPlayer !== null) this.stopCarouselVideo(this.activeVideoPlayer);
      this.currentIndex = this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
      this.updateCarousel();
      this.handleMetaVideoOnSlideChange(this.currentIndex);
      this.checkAndAutoPlayVideo(this.currentIndex);
    }

    goToSlide(index) {
      if (this.activeVideoPlayer !== null) this.stopCarouselVideo(this.activeVideoPlayer);
      this.currentIndex = index;
      this.updateCarousel();
      this.handleMetaVideoOnSlideChange(this.currentIndex);
      this.checkAndAutoPlayVideo(this.currentIndex);
    }

    updateCarousel() {
      this.slides.forEach((s, i) => s.classList.toggle('active', i === this.currentIndex));
      this.dots.forEach((d, i)   => d.classList.toggle('active', i === this.currentIndex));
    }

    openModal() {
      this.ensureModalReady();
      if (!this.modal) return;

      this.stopAllVideos();
      this.modalIndex  = this.currentIndex;
      this.isModalOpen = true;

      if (!this.modalBuilt) {
        this.createModalSlides();
        this.modalBuilt = true;
      }

      this.modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.showModalSlide(this.modalIndex);
    }

    invalidateModalCache() {
      this.modalBuilt = false;
      if (this.modalSlideContainer) this.modalSlideContainer.innerHTML = '';
      const dots = this.modal ? this.modal.querySelector('[data-modal-dots]') : null;
      if (dots) dots.innerHTML = '';
      this.modalSlides = [];
    }

    createModalSlides() {
      if (!this.modalSlideContainer) return;
      this.modalSlideContainer.innerHTML = '';
      this.modalSlides = [];

      this.slides.forEach((slide, index) => {
        const isProductMedia   = slide.getAttribute('data-is-product-media') === 'true';
        const slideType        = slide.getAttribute('data-slide-type');
        const fullscreenVideo  = slide.getAttribute('data-fullscreen-video');
        const fullscreenOverlay = slide.getAttribute('data-fullscreen-overlay');
        const fullscreenVideoThumb = slide.getAttribute('data-fullscreen-video-thumb');

        const modalSlide = document.createElement('div');
        modalSlide.className = 'modal-slide';
        modalSlide.setAttribute('data-modal-index', index);

        let content = '';

        if (slideType === 'mp4-video') {
          const videoEl  = slide.querySelector('video');
          const sourceEl = videoEl?.querySelector('source');
          const src = videoEl ? (videoEl.currentSrc || sourceEl?.src || '') : '';
          if (src) {
            content = `<div class="modal-mp4-wrapper">
              <video autoplay loop playsinline controls
                     style="max-width:100%;max-height:100%;object-fit:contain;">
                <source src="${src}" type="video/mp4">
              </video>
            </div>`;
          }

        } else if (slideType === 'video' && fullscreenVideo) {
          let videoId = '';
          if (fullscreenVideo.includes('youtube.com/watch?v='))
            videoId = fullscreenVideo.split('v=')[1].split('&')[0];
          else if (fullscreenVideo.includes('youtu.be/'))
            videoId = fullscreenVideo.split('youtu.be/')[1].split('?')[0];
          if (videoId) {
            const thumb = fullscreenVideoThumb || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
            content = `<div class="modal-video-wrapper" data-modal-video-wrapper data-video-index="${index}">
              <div class="modal-video-thumbnail"><img src="${thumb}" alt="Video thumbnail"></div>
            </div>`;
          }

        } else {
          let fullscreenImage = '';
          if (isProductMedia) {
            const mi = parseInt(slide.getAttribute('data-media-index') || '0', 10);
            fullscreenImage = this.getFullscreenImageForProductMedia(mi);
          } else {
            fullscreenImage = slide.getAttribute('data-fullscreen-image');
          }
          if (fullscreenImage) {
            content = `<div class="modal-image-zoom-wrapper" data-zoom-wrapper>
              <img src="${fullscreenImage}" class="modal-image" data-zoom-image alt="">
            </div>
            <div class="modal-zoom-controls" data-zoom-controls>
              <button class="modal-zoom-btn" type="button" data-zoom-out>-</button>
              <button class="modal-zoom-btn" type="button" data-zoom-reset>100%</button>
              <button class="modal-zoom-btn" type="button" data-zoom-in>+</button>
            </div>`;
          }
        }

        if (fullscreenOverlay && fullscreenOverlay !== 'null' && fullscreenOverlay !== '') {
          content += `<div class="modal-overlay-box"><div class="overlay-content">${fullscreenOverlay}</div></div>`;
        }

        modalSlide.innerHTML = content;
        this.modalSlideContainer.appendChild(modalSlide);
        this.modalSlides.push(modalSlide);
      });

      this.buildModalDots();
      this.setupModalZoom();
    }

    buildModalDots() {
      if (!this.modal) return;
      const dotsContainer = this.modal.querySelector('[data-modal-dots]');
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';
      this.modalSlides.forEach((_, i) => {
        const dot = document.createElement('div');
        dot.className = 'modal-dot' + (i === this.modalIndex ? ' active' : '');
        dot.addEventListener('click', () => this.showModalSlide(i));
        dotsContainer.appendChild(dot);
      });
    }

    updateModalDots() {
      if (!this.modal) return;
      const dots = this.modal.querySelectorAll('.modal-dot');
      dots.forEach((d, i) => d.classList.toggle('active', i === this.modalIndex));
    }

    setupModalZoom() {
      this.modalSlides.forEach((slide, index) => {
        const zoomWrapper  = slide.querySelector('[data-zoom-wrapper]');
        const zoomInBtn    = slide.querySelector('[data-zoom-in]');
        const zoomOutBtn   = slide.querySelector('[data-zoom-out]');
        const zoomResetBtn = slide.querySelector('[data-zoom-reset]');

        if (zoomInBtn)    zoomInBtn.addEventListener('click',    (e) => { e.stopPropagation(); this.changeModalZoom(index,  this.zoomStep); });
        if (zoomOutBtn)   zoomOutBtn.addEventListener('click',   (e) => { e.stopPropagation(); this.changeModalZoom(index, -this.zoomStep); });
        if (zoomResetBtn) zoomResetBtn.addEventListener('click', (e) => { e.stopPropagation(); this.resetModalZoom(index); });

        if (!zoomWrapper) return;

        zoomWrapper.addEventListener('wheel', (e) => {
          e.preventDefault();
          this.changeModalZoom(index, e.deltaY < 0 ? this.zoomStep : -this.zoomStep);
        }, { passive: false });

        zoomWrapper.addEventListener('dblclick', (e) => {
          e.preventDefault();
          this.getModalZoom(index) > 1 ? this.resetModalZoom(index) : this.setModalZoom(index, 2);
        });

        zoomWrapper.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 2) return;
          this.modalTouchState.set(index, {
            isPinching:    true,
            startDistance: this.getTouchDistance(e.touches[0], e.touches[1]),
            startZoom:     this.getModalZoom(index)
          });
        }, { passive: true });

        zoomWrapper.addEventListener('touchmove', (e) => {
          if (e.touches.length !== 2) return;
          const ts = this.modalTouchState.get(index);
          if (!ts?.isPinching) return;
          e.preventDefault();
          this.setModalZoom(index, ts.startZoom * (this.getTouchDistance(e.touches[0], e.touches[1]) / ts.startDistance));
        }, { passive: false });

        zoomWrapper.addEventListener('touchend', () => {
          const ts = this.modalTouchState.get(index);
          if (ts) { ts.isPinching = false; this.modalTouchState.set(index, ts); }
        }, { passive: true });
      });
    }

    getTouchDistance(a, b) {
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }

    getModalZoom(index)      { return this.modalZoomLevels.get(index) ?? 1; }
    changeModalZoom(i, d)    { this.setModalZoom(i, this.getModalZoom(i) + d); }
    resetModalZoom(i)        { this.setModalZoom(i, 1); }
    resetAllModalZoom()      { this.modalSlides.forEach((_, i) => this.resetModalZoom(i)); this.modalTouchState.clear(); }

    setModalZoom(index, value) {
      const slide = this.modalSlides[index];
      if (!slide) return;
      const img = slide.querySelector('[data-zoom-image]');
      const btn = slide.querySelector('[data-zoom-reset]');
      if (!img) return;
      const clamped = Math.min(this.maxZoomLevel, Math.max(this.minZoomLevel, value));
      this.modalZoomLevels.set(index, clamped);
      img.style.transform = `scale(${clamped})`;
      img.classList.toggle('is-zoomed', clamped > 1);
      if (btn) btn.textContent = `${Math.round(clamped * 100)}%`;
    }

    showModalSlide(index) {
      this.modalIndex = index;

      this.modalSlides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('active', isActive);
        if (isActive) return;
        const vid = slide.querySelector('video');
        if (vid) { vid.pause(); vid.currentTime = 0; }
        const wrapper = slide.querySelector('[data-modal-video-wrapper]');
        if (wrapper) {
          wrapper.querySelector('iframe')?.remove();
          const thumb = wrapper.querySelector('.modal-video-thumbnail');
          if (thumb) { thumb.style.display = 'flex'; thumb.style.opacity = '1'; }
        }
      });

      this.resetAllModalZoom();
      this.updateModalDots();

      if (this.modalVideoTimeouts.has(index)) {
        clearTimeout(this.modalVideoTimeouts.get(index));
        this.modalVideoTimeouts.delete(index);
      }

      const currentSlide  = this.slides[index];
      if (!currentSlide) return;

      const slideType       = currentSlide.getAttribute('data-slide-type');
      const fullscreenAutoplay = currentSlide.getAttribute('data-fullscreen-autoplay') === 'true';

      if (slideType === 'mp4-video') {
        const modalVid = this.modalSlides[index]?.querySelector('video');
        if (modalVid) { modalVid.muted = true; modalVid.play().catch(() => {}); }
        return;
      }

      if (slideType !== 'video' || !fullscreenAutoplay) return;

      const modalSlide   = this.modalSlides[index];
      const videoWrapper = modalSlide?.querySelector('[data-modal-video-wrapper]');
      const thumbnail    = modalSlide?.querySelector('.modal-video-thumbnail');
      if (!videoWrapper || !thumbnail) return;

      const fullscreenVideo = currentSlide.getAttribute('data-fullscreen-video');
      let videoId = '';
      if (fullscreenVideo.includes('youtube.com/watch?v='))
        videoId = fullscreenVideo.split('v=')[1].split('&')[0];
      else if (fullscreenVideo.includes('youtu.be/'))
        videoId = fullscreenVideo.split('youtu.be/')[1].split('?')[0];
      if (!videoId) return;

      thumbnail.style.display = 'flex';
      thumbnail.style.opacity = '1';

      this.modalVideoTimeouts.set(index, setTimeout(() => {
        const iframe = document.createElement('iframe');
        iframe.className   = 'modal-video-iframe';
        iframe.src         = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1&enablejsapi=1`;
        iframe.allow       = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;border:none;opacity:0;';
        videoWrapper.appendChild(iframe);
        requestAnimationFrame(() => {
          iframe.style.opacity = '1';
          setTimeout(() => { thumbnail.style.opacity = '0'; }, 300);
        });
      }, 2000));
    }

    modalPrev() {
      if (this.modalVideoTimeouts.has(this.modalIndex)) {
        clearTimeout(this.modalVideoTimeouts.get(this.modalIndex));
        this.modalVideoTimeouts.delete(this.modalIndex);
      }
      this.showModalSlide(this.modalIndex === 0 ? this.modalSlides.length - 1 : this.modalIndex - 1);
    }

    modalNext() {
      if (this.modalVideoTimeouts.has(this.modalIndex)) {
        clearTimeout(this.modalVideoTimeouts.get(this.modalIndex));
        this.modalVideoTimeouts.delete(this.modalIndex);
      }
      this.showModalSlide(this.modalIndex === this.modalSlides.length - 1 ? 0 : this.modalIndex + 1);
    }

    closeModal() {
      this.modalVideoTimeouts.forEach((id) => clearTimeout(id));
      this.modalVideoTimeouts.clear();
      this.resetAllModalZoom();
      this.modalSlideContainer?.querySelectorAll('iframe').forEach((iframe) => {
        iframe.src = iframe.src.replace(/(autoplay=)\d+/, '$10');
      });
      this.modalSlideContainer?.querySelectorAll('video').forEach((v) => { v.pause(); v.currentTime = 0; });
      this.modal.classList.remove('is-open');
      document.body.style.overflow = '';
      this.isModalOpen = false;
      this.currentIndex = this.modalIndex;
      this.updateCarousel();
      this.handleMetaVideoOnSlideChange(this.currentIndex);
      this.checkAndAutoPlayVideo(this.currentIndex);
    }
  }

  function initCarousel() {
    const container = document.querySelector('[data-product-media-container]');
    if (!container || container._carouselInit) return;
    container._carouselInit = true;
    new ProductCarousel(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousel);
  } else {
    initCarousel();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      const container = document.querySelector('[data-product-media-container]');
      if (container) {
        delete container._carouselInit;
        initCarousel();
      }
    });
  }
})();
